import { ComponentType } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  Injectable,
  Injector,
  inject,
} from '@angular/core';

import { focusElement } from '@plugins/client/utils';

import { DialogComponent } from './dialog.component';
import { DIALOG_DATA, DIALOG_CONFIG, COMMON_DIALOG_CONFIG } from './tokens';
import { DialogConfig } from './types';

const DEFAULT_DIALOG_CONFIG: DialogConfig = {
  modal: true,
  backdrop: false,
  verticalOffset: 25,
  verticalOffsetDisabled: false,
};

@Injectable()
export class DialogService {
  #document = inject(DOCUMENT);
  #cfr = inject(ComponentFactoryResolver);
  #appRef = inject(ApplicationRef);
  #injector = inject(Injector);
  #commonConfig = inject(COMMON_DIALOG_CONFIG, { optional: true }) ?? {};

  open<T, K>(
    component: ComponentType<T>,
    data?: K,
    config: Partial<DialogConfig> = {},
  ) {
    const previousFocusedElement = this.#document
      .activeElement as HTMLElement | null;
    previousFocusedElement?.blur();

    const injector = Injector.create({
      parent: this.#injector,
      providers: [
        {
          provide: DIALOG_DATA,
          useValue: data,
        },
        {
          provide: DIALOG_CONFIG,
          useValue: {
            ...DEFAULT_DIALOG_CONFIG,
            ...this.#commonConfig,
            ...config,
          },
        },
      ],
    });
    const dialogRef = this.#cfr
      .resolveComponentFactory(DialogComponent)
      .create(injector);
    this.#appRef.attachView(dialogRef.hostView);
    const domElem = (dialogRef.hostView as EmbeddedViewRef<unknown>)
      .rootNodes[0] as HTMLElement;

    this.#document.body.appendChild(domElem);

    const close = () => {
      this.#appRef.detachView(dialogRef.hostView);
      dialogRef.destroy();
      focusElement(previousFocusedElement);
    };

    const componentRef = dialogRef.instance.open(component, close);
    return {
      dialog: dialogRef.instance,
      component: componentRef.instance,
      close,
    };
  }
}
